import React, { useEffect, useMemo, useState } from "react";
import {
  useGetContactListContactsQuery,
  useRemoveContactFromListMutation,
} from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Plus, Trash2, Search, Users } from "lucide-react";
import { toast } from "sonner";
import AddContactsModal from "./AddContactsModal";
import ExportDialog from "@/components/exports/ExportDialog";
import { useCanExport } from "@/hooks/useCanExport";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  listId: string;
}

function TableSkeleton() {
  return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 rounded-md bg-muted animate-pulse" />
        ))}
      </div>
  );
}

export default function ContactListMembersSection({ listId }: Props) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [q, setQ] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { canExport } = useCanExport();

  // Reset page when q/size changes
  useEffect(() => {
    setPage(0);
  }, [q, size]);

  const { data, isLoading, isFetching, error } = useGetContactListContactsQuery({
    listId,
    page,
    size,
    q: q.trim() || undefined,
    sort: "createdAt,desc",
  });

  const [removeContact, { isLoading: isRemoving }] = useRemoveContactFromListMutation();

  const members = data?.items ?? [];
  const total = data?.totalElements ?? 0;

  const canPrev = page > 0 && total > 0 && !isFetching;
  const canNext = !data?.last && total > 0 && !isFetching;

  const headerSubtitle = useMemo(() => {
    if (isLoading) return "Loading members…";
    if (error) return "Failed to load members";
    if (total === 0) return "No contacts in this list yet";
    return `${total} member${total === 1 ? "" : "s"}`;
  }, [isLoading, error, total]);

  const handleRemove = async (contactId?: string) => {
    if (!contactId) return;
    try {
      await removeContact({ listId, contactId }).unwrap();
      toast.success("Removed from list");
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to remove contact");
    }
  };

  return (
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">Contacts</h3>
                <Badge variant="secondary" className="shrink-0">
                  {total}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{headerSubtitle}</p>
            </div>

            <div className="flex items-center gap-2">
              {canExport && (
                <Button size="sm" variant="outline" onClick={() => setExportOpen(true)} disabled={total === 0}>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              )}
              <Button size="sm" onClick={() => setIsModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Contacts
              </Button>
            </div>
          </div>

          {/* Controls row */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search members..."
                  className="pl-9"
              />
            </div>

            <div className="flex items-center gap-2 justify-between sm:justify-end">
              <span className="text-xs text-muted-foreground">Rows</span>
              <Select value={String(size)} onValueChange={(v) => setSize(parseInt(v, 10))}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>

              <Badge variant="outline" className="hidden sm:inline-flex">
                Page {page + 1} / {Math.max(data?.totalPages ?? 1, 1)}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
              <TableSkeleton />
          ) : error ? (
              <div className="py-10 text-center text-sm text-destructive">
                Failed to load contacts for this list.
              </div>
          ) : members.length === 0 ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Users className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="text-sm font-medium">No contacts yet</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Add contacts to start using this list in campaigns.
                </div>
                <Button className="mt-4" onClick={() => setIsModalOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Contacts
                </Button>
              </div>
          ) : (
              <>
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {members.map((item) => {
                        const c = item.contact;
                        const name = [c?.firstName, c?.lastName].filter(Boolean).join(" ") || "—";

                        return (
                            <TableRow key={item.id}>
                              <TableCell className="font-medium">
                                <div className="min-w-0">
                                  <div className="truncate">{name}</div>
                                  {c?.position && (
                                      <div className="text-xs text-muted-foreground truncate">
                                        {c.position}
                                      </div>
                                  )}
                                </div>
                              </TableCell>

                              <TableCell>
                                <span className="text-sm text-muted-foreground">{c?.company || "—"}</span>
                              </TableCell>

                              <TableCell className="text-right">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={isRemoving}
                                    onClick={() => handleRemove(c?.id)}
                                    title="Remove from list"
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <div className="text-xs text-muted-foreground">
                    {isFetching ? "Updating…" : `Showing ${members.length} of ${total}`}
                  </div>

                  <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!canPrev}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      Prev
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!canNext}
                        onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
          )}
        </CardContent>

        <AddContactsModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            listId={listId}
        />

        <ExportDialog
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            title="Export list members"
            description="Every member of this list, with the same columns as the leads export."
            request={{ kind: "LIST", listId }}
            allowColumnChoice
        />
      </Card>
  );
}